/** @fileoverview ClubOS Finance Dashboard — Controllers / CFO / Finance Manager view. */
/* eslint-disable react/prop-types */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftProgress from "components/SoftProgress";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ClickableCard from "components/ClickableCard";

// ClubOS data & utilities
import { financialSummary, expenseEntries, taxRecords, departmentBudgets } from "data/mockData";
import { formatCompact, CLUB_NAME, SEASON } from "utils/indiaLocale";

// ─── DERIVED DATA ──────────────────────────────────────────────────────────────

const ffpPct = Math.round((financialSummary.ffpUsed / financialSummary.ffpAllowable) * 100);

const budgetVsActualChart = {
  labels: financialSummary.budgetVsActual.map((b) => b.month),
  datasets: [
    { label: "Budget", color: "info",  data: financialSummary.budgetVsActual.map((b) => Math.round(b.budget / 1_00_000)) },
    { label: "Actual", color: "error", data: financialSummary.budgetVsActual.map((b) => Math.round(b.actual / 1_00_000)) },
  ],
};

const revenueDonutChart = {
  labels: financialSummary.revenueBreakdown.map((r) => r.label),
  datasets: { label: "Revenue (₹L)", backgroundColors: ["info","primary","success","warning","error"], data: financialSummary.revenueBreakdown.map((r) => Math.round(r.value / 1_00_000)) },
};

const wageDonutChart = {
  labels: financialSummary.wageBillBreakdown.map((w) => w.label),
  datasets: { label: "Monthly Wage (₹L)", backgroundColors: ["info","primary","dark","secondary","warning"], data: financialSummary.wageBillBreakdown.map((w) => Math.round(w.value / 1_00_000)) },
};

// ─── KPI DETAIL DEFINITIONS ────────────────────────────────────────────────────

const KPI_DETAILS = {
  revenue: {
    title: "Annual Revenue Breakdown", icon: "trending_up", iconColor: "success",
    content: () => (
      <SoftBox>
        <SoftTypography variant="body2" mb={1}>Total: <strong>{formatCompact(financialSummary.annualRevenue)}</strong></SoftTypography>
        {financialSummary.revenueBreakdown.map((r) => (
          <SoftBox key={r.label} display="flex" justifyContent="space-between" mb={0.5}>
            <SoftTypography variant="caption">{r.label}</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold">{formatCompact(r.value)}</SoftTypography>
          </SoftBox>
        ))}
        <Divider sx={{ my: 1 }} />
        <SoftTypography variant="caption" color="secondary">ISL central pool + commercial partnerships + matchday + broadcasting rights.</SoftTypography>
      </SoftBox>
    ),
  },
  expenditure: {
    title: "Monthly Expenditure Detail", icon: "payments", iconColor: "error",
    content: () => (
      <SoftBox>
        <SoftTypography variant="body2" mb={1}>Monthly Total: <strong>{formatCompact(financialSummary.totalMonthlySalaryBill)}</strong></SoftTypography>
        {financialSummary.wageBillBreakdown.map((w) => (
          <SoftBox key={w.label} display="flex" justifyContent="space-between" mb={0.5}>
            <SoftTypography variant="caption">{w.label}</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold">{formatCompact(w.value)}</SoftTypography>
          </SoftBox>
        ))}
        <Divider sx={{ my: 1 }} />
        <SoftTypography variant="caption" color="secondary">Payroll includes player wages, staff salaries, and operational costs.</SoftTypography>
      </SoftBox>
    ),
  },
  ffp: {
    title: "FFP Headroom Detail", icon: "policy", iconColor: "warning",
    content: () => {
      const headroom = financialSummary.ffpAllowable - financialSummary.ffpUsed;
      return (
        <SoftBox>
          {[["FFP Allowable", financialSummary.ffpAllowable, null], ["FFP Used", financialSummary.ffpUsed, "error"], ["Remaining Headroom", headroom, "success"]].map(([label, val, color]) => (
            <SoftBox key={label} display="flex" justifyContent="space-between" mb={0.5}>
              <SoftTypography variant="caption">{label}</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color={color || "text"}>{formatCompact(val)}</SoftTypography>
            </SoftBox>
          ))}
          <SoftProgress value={ffpPct} color={ffpPct > 85 ? "error" : "warning"} variant="gradient" />
          <SoftTypography variant="caption" color="secondary" mt={1} sx={{ display: "block" }}>{ffpPct}% of FFP limit consumed. Limit resets at end of season.</SoftTypography>
        </SoftBox>
      );
    },
  },
  budget: {
    title: "Transfer Budget Status", icon: "swap_horiz", iconColor: "info",
    content: () => {
      const remaining = financialSummary.transferBudget - financialSummary.transferBudgetUsed;
      const usedPct = Math.round((financialSummary.transferBudgetUsed / financialSummary.transferBudget) * 100);
      return (
        <SoftBox>
          {[["Total Transfer Budget", financialSummary.transferBudget, null], ["Spent", financialSummary.transferBudgetUsed, "error"], ["Remaining", remaining, "success"]].map(([label, val, color]) => (
            <SoftBox key={label} display="flex" justifyContent="space-between" mb={0.5}>
              <SoftTypography variant="caption">{label}</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color={color || "text"}>{formatCompact(val)}</SoftTypography>
            </SoftBox>
          ))}
          <SoftProgress value={usedPct} color="info" variant="gradient" />
          <SoftTypography variant="caption" color="secondary" mt={1} sx={{ display: "block" }}>{usedPct}% of transfer budget utilised this window.</SoftTypography>
        </SoftBox>
      );
    },
  },
};

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────────────

const COLOR_MAP = { info:"#17c1e8", primary:"#cb0c9f", success:"#82d616", warning:"#fbcf33", error:"#ea0606", dark:"#344767", secondary:"#8392ab" };

function DonutLegend({ labels, colors }) {
  return (
    <SoftBox display="flex" flexWrap="wrap" gap={1} mt={1.5} justifyContent="center">
      {labels.map((label, i) => (
        <SoftBox key={label} display="flex" alignItems="center" gap={0.5}>
          <SoftBox component="span" sx={{ width:10, height:10, borderRadius:"50%", background: COLOR_MAP[colors[i % colors.length]] || "#999", flexShrink:0 }} />
          <SoftTypography variant="caption" color="secondary">{label}</SoftTypography>
        </SoftBox>
      ))}
    </SoftBox>
  );
}

function DeptBudgetTable({ rows }) {
  return (
    <SoftBox sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>{["Department","Annual Budget","YTD Spent","Remaining","% Used","Status"].map((h) => (
            <th key={h} style={{ textAlign:"left", padding:"6px 8px" }}><SoftTypography variant="caption" color="secondary" fontWeight="bold">{h}</SoftTypography></th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((dept) => {
            const pct = Math.round((dept.ytdSpent / dept.annualBudget) * 100);
            const rem = dept.annualBudget - dept.ytdSpent;
            const sc = pct > 90 ? "error" : pct > 75 ? "warning" : "success";
            return (
              <tr key={dept.department} style={{ borderTop: "1px solid #f0f0f0" }}>
                <td style={{ padding:"8px" }}><SoftTypography variant="caption" fontWeight="medium">{dept.department}</SoftTypography></td>
                <td style={{ padding:"8px" }}><SoftTypography variant="caption">{formatCompact(dept.annualBudget)}</SoftTypography></td>
                <td style={{ padding:"8px" }}><SoftTypography variant="caption">{formatCompact(dept.ytdSpent)}</SoftTypography></td>
                <td style={{ padding:"8px" }}><SoftTypography variant="caption" color={rem < 0 ? "error" : "text"}>{formatCompact(Math.abs(rem))}{rem < 0 ? " over" : ""}</SoftTypography></td>
                <td style={{ padding:"8px", minWidth:100 }}>
                  <SoftBox display="flex" alignItems="center" gap={1}><SoftBox flex={1}><SoftProgress value={Math.min(pct, 100)} color={sc} variant="gradient" /></SoftBox><SoftTypography variant="caption" color="secondary">{pct}%</SoftTypography></SoftBox>
                </td>
                <td style={{ padding:"8px" }}><SoftBadge badgeContent={pct > 90 ? "Over Risk" : pct > 75 ? "Near Limit" : "On Track"} color={sc} variant="gradient" size="xs" container /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SoftBox>
  );
}

const hoverSx = { cursor:"pointer", borderRadius:"lg", transition:"transform 0.18s, box-shadow 0.18s", "&:hover":{ transform:"translateY(-3px)", boxShadow:"0 10px 28px rgba(0,0,0,0.13)" } };

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

function FinanceDashboard() {
  const [kpiModal, setKpiModal] = useState(null);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">Finance Dashboard</SoftTypography>
          <SoftTypography variant="body2" color="secondary">{CLUB_NAME} · Finance Team · Season {SEASON}</SoftTypography>
        </SoftBox>

        {/* ── Row 1: KPI cards ─────────────────────────────────────────── */}
        <Grid container spacing={3} mb={3}>
          {[
            { key:"revenue", title:"Annual Revenue", count: formatCompact(financialSummary.annualRevenue), pct:{ color:"success", text:"ISL + commercial" }, icon:{ component:"trending_up", color:"success" } },
            { key:"expenditure", title:"Monthly Expenditure", count: formatCompact(financialSummary.totalMonthlySalaryBill), pct:{ color:"error", text:"payroll + ops" }, icon:{ component:"payments", color:"error" } },
            { key:"ffp", title:"FFP Headroom", count: formatCompact(financialSummary.ffpAllowable - financialSummary.ffpUsed), pct:{ color:"warning", text:`${ffpPct}% used` }, icon:{ component:"policy", color:"warning" } },
            { key:"budget", title:"Transfer Budget Left", count: formatCompact(financialSummary.transferBudget - financialSummary.transferBudgetUsed), pct:{ color:"info", text:`of ${formatCompact(financialSummary.transferBudget)}` }, icon:{ component:"swap_horiz", color:"info" } },
          ].map(({ key, title, count, pct, icon }) => (
            <Grid key={key} item xs={12} sm={6} lg={3}>
              <SoftBox onClick={() => setKpiModal(key)} sx={hoverSx}>
                <MiniStatisticsCard title={{ text: title }} count={count} percentage={pct} icon={icon} />
              </SoftBox>
            </Grid>
          ))}
        </Grid>

        {/* ── Row 2: Budget vs Actual + Revenue Donut ──────────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={8}>
            <ClickableCard
              title="Budget vs Actual Spend — Full Data" icon="bar_chart" iconColor="info"
              tooltip="Click to view full monthly variance table"
              detailContent={
                <SoftBox sx={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Month","Budget (₹L)","Actual (₹L)","Variance (₹L)","Status"].map((h) => (
                      <th key={h} style={{ textAlign:"left", padding:"6px 8px" }}><SoftTypography variant="caption" color="secondary" fontWeight="bold">{h}</SoftTypography></th>
                    ))}</tr></thead>
                    <tbody>{financialSummary.budgetVsActual.map((b) => {
                      const bL = Math.round(b.budget / 1_00_000), aL = Math.round(b.actual / 1_00_000), vL = bL - aL;
                      return (
                        <tr key={b.month} style={{ borderTop:"1px solid #f0f0f0" }}>
                          <td style={{ padding:"8px" }}><SoftTypography variant="caption" fontWeight="medium">{b.month}</SoftTypography></td>
                          <td style={{ padding:"8px" }}><SoftTypography variant="caption">{bL}</SoftTypography></td>
                          <td style={{ padding:"8px" }}><SoftTypography variant="caption">{aL}</SoftTypography></td>
                          <td style={{ padding:"8px" }}><SoftTypography variant="caption" color={vL < 0 ? "error" : "success"}>{vL < 0 ? `▲ ${Math.abs(vL)} over` : `▼ ${vL} under`}</SoftTypography></td>
                          <td style={{ padding:"8px" }}><SoftBadge badgeContent={vL < 0 ? "Over" : "On Track"} color={vL < 0 ? "error" : "success"} variant="gradient" size="xs" container /></td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </SoftBox>
              }
            >
              <GradientLineChart title="Budget vs Actual Spend" description="Monthly figures in ₹ Lakhs" chart={budgetVsActualChart} />
            </ClickableCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <ClickableCard
              title="Revenue Breakdown — Detail" icon="pie_chart" iconColor="primary"
              tooltip="Click to view full revenue breakdown"
              detailContent={
                <SoftBox>
                  {financialSummary.revenueBreakdown.map((r) => (
                    <SoftBox key={r.label} display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption">{r.label}</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">{formatCompact(r.value)}</SoftTypography>
                    </SoftBox>
                  ))}
                  <Divider sx={{ my:1 }} />
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftTypography variant="caption" fontWeight="bold">Total</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold">{formatCompact(financialSummary.annualRevenue)}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              }
            >
              <SoftBox p={3} display="flex" flexDirection="column" alignItems="center">
                <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>Revenue Breakdown</SoftTypography>
                <SoftTypography variant="caption" color="secondary" mb={1}>Annual · ₹ Lakhs</SoftTypography>
                <Divider sx={{ width:"100%", mb:1 }} />
                <DefaultDoughnutChart chart={revenueDonutChart} />
                <DonutLegend labels={revenueDonutChart.labels} colors={revenueDonutChart.datasets.backgroundColors} />
              </SoftBox>
            </ClickableCard>
          </Grid>
        </Grid>

        {/* ── Row 3: Department Budgets + Tax Compliance ───────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={7}>
            <ClickableCard
              title="Department Budgets — All Departments" icon="account_balance" iconColor="dark"
              tooltip="Click to view all 10 department budgets with remaining"
              detailContent={<DeptBudgetTable rows={departmentBudgets} />}
            >
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="medium">Department Budgets</SoftTypography>
                <SoftTypography variant="caption" color="secondary">Annual budget vs YTD spend</SoftTypography>
                <Divider sx={{ my:1 }} />
                <DeptBudgetTable rows={departmentBudgets.slice(0, 5)} />
                <SoftTypography variant="caption" color="secondary" mt={1} sx={{ display:"block" }}>Showing top 5 — click to expand all.</SoftTypography>
              </SoftBox>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} lg={5}>
            <ClickableCard
              title="Tax Compliance — All Quarters" icon="receipt_long" iconColor="warning"
              tooltip="Click to view all 4 quarters with amounts"
              detailContent={
                <SoftBox>
                  {taxRecords.map((rec) => (
                    <SoftBox key={rec.period} mb={2}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <SoftTypography variant="caption" fontWeight="medium">{rec.period}</SoftTypography>
                        <SoftBadge badgeContent={rec.status} color={rec.status === "Filed" ? "success" : "warning"} variant="gradient" size="xs" container />
                      </SoftBox>
                      <SoftBox display="flex" gap={3}>
                        <SoftTypography variant="caption" color="secondary">TDS: <strong>{formatCompact(rec.tdsDeducted)}</strong></SoftTypography>
                        <SoftTypography variant="caption" color="secondary">GST: <strong>{formatCompact(rec.gstCollected)}</strong></SoftTypography>
                        <SoftTypography variant="caption" color="secondary">Total: <strong>{formatCompact(rec.tdsDeducted + rec.gstCollected)}</strong></SoftTypography>
                      </SoftBox>
                      <Divider sx={{ mt:1 }} />
                    </SoftBox>
                  ))}
                  <SoftBox display="flex" alignItems="center" gap={1} mt={1}>
                    <Icon fontSize="small" sx={{ color:"warning.main" }}>info_outline</Icon>
                    <SoftTypography variant="caption" color="secondary">Q4 filing due before 31 May. Ensure all invoices are uploaded.</SoftTypography>
                  </SoftBox>
                </SoftBox>
              }
            >
              <SoftBox p={3}>
                <SoftTypography variant="h6" fontWeight="medium">Tax Compliance</SoftTypography>
                <SoftTypography variant="caption" color="secondary">Quarterly filings · TDS & GST</SoftTypography>
                <Divider sx={{ my:1 }} />
                {taxRecords.map((rec) => (
                  <SoftBox key={rec.period} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <SoftTypography variant="caption" fontWeight="medium">{rec.period}</SoftTypography>
                    <SoftBadge badgeContent={rec.status} color={rec.status === "Filed" ? "success" : "warning"} variant="gradient" size="xs" container />
                  </SoftBox>
                ))}
                <SoftBox display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Icon fontSize="small" sx={{ color:"warning.main" }}>info_outline</Icon>
                  <SoftTypography variant="caption" color="secondary">Click to view TDS / GST amounts per quarter.</SoftTypography>
                </SoftBox>
              </SoftBox>
            </ClickableCard>
          </Grid>
        </Grid>

        {/* ── Row 4: Recent Expenses + Wage Distribution ───────────────── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={6}>
            <ClickableCard
              title="All Expenses" icon="receipt" iconColor="error"
              tooltip="Click to view all 14 expense entries"
              detailContent={
                <SoftBox>
                  {expenseEntries.map((entry, idx) => (
                    <SoftBox key={entry.id ?? idx} mb={1}>
                      <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                        <SoftBox flex={1} pr={1}>
                          <SoftTypography variant="caption" fontWeight="medium" sx={{ display:"block" }}>{entry.description}</SoftTypography>
                          <SoftTypography variant="caption" color="secondary">{entry.date} · {entry.category}</SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                          <SoftTypography variant="caption" fontWeight="bold">{formatCompact(entry.amount)}</SoftTypography>
                          <SoftBadge badgeContent={entry.status} color={entry.status === "Approved" || entry.status === "Paid" ? "success" : entry.status === "Pending" ? "warning" : "error"} variant="gradient" size="xs" container />
                        </SoftBox>
                      </SoftBox>
                      {idx < expenseEntries.length - 1 && <Divider sx={{ mt:1, mb:0 }} />}
                    </SoftBox>
                  ))}
                </SoftBox>
              }
            >
              <SoftBox p={3}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <SoftBox>
                    <SoftTypography variant="h6" fontWeight="medium">Recent Expenses</SoftTypography>
                    <SoftTypography variant="caption" color="secondary">Latest entries — pending & processed</SoftTypography>
                  </SoftBox>
                  <SoftButton variant="text" color="info" size="small"><Icon>open_in_new</Icon>&nbsp;View All</SoftButton>
                </SoftBox>
                <Divider sx={{ my:1 }} />
                {expenseEntries.slice(0, 6).map((entry, idx) => (
                  <SoftBox key={entry.id ?? idx} mb={1}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                      <SoftBox flex={1} pr={1}>
                        <SoftTypography variant="caption" fontWeight="medium" sx={{ display:"block" }}>
                          {entry.description?.length > 42 ? `${entry.description.slice(0, 42)}…` : entry.description}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="secondary">{entry.date} · {entry.category}</SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                        <SoftTypography variant="caption" fontWeight="bold">{formatCompact(entry.amount)}</SoftTypography>
                        <SoftBadge badgeContent={entry.status} color={entry.status === "Approved" || entry.status === "Paid" ? "success" : entry.status === "Pending" ? "warning" : "error"} variant="gradient" size="xs" container />
                      </SoftBox>
                    </SoftBox>
                    {idx < 5 && <Divider sx={{ mt:1, mb:0 }} />}
                  </SoftBox>
                ))}
              </SoftBox>
            </ClickableCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <ClickableCard
              title="Wage Distribution — Full Breakdown" icon="people" iconColor="info"
              tooltip="Click to view full wage distribution by category"
              detailContent={
                <SoftBox>
                  {financialSummary.wageBillBreakdown.map((w) => (
                    <SoftBox key={w.label} display="flex" justifyContent="space-between" mb={0.5}>
                      <SoftTypography variant="caption">{w.label}</SoftTypography>
                      <SoftTypography variant="caption" fontWeight="bold">{formatCompact(w.value)}</SoftTypography>
                    </SoftBox>
                  ))}
                  <Divider sx={{ my:1 }} />
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftTypography variant="caption" fontWeight="bold">Total Monthly</SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold">{formatCompact(financialSummary.totalMonthlySalaryBill)}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              }
            >
              <SoftBox p={3} display="flex" flexDirection="column" alignItems="center">
                <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>Wage Distribution</SoftTypography>
                <SoftTypography variant="caption" color="secondary" mb={1}>Monthly bill by category · ₹ Lakhs</SoftTypography>
                <Divider sx={{ width:"100%", mb:1 }} />
                <DefaultDoughnutChart chart={wageDonutChart} />
                <DonutLegend labels={wageDonutChart.labels} colors={wageDonutChart.datasets.backgroundColors} />
                <SoftBox mt={2} width="100%" display="flex" justifyContent="space-between">
                  <SoftTypography variant="caption" color="secondary">Total Monthly Wage Bill</SoftTypography>
                  <SoftTypography variant="caption" fontWeight="bold">{formatCompact(financialSummary.totalMonthlySalaryBill)}</SoftTypography>
                </SoftBox>
              </SoftBox>
            </ClickableCard>
          </Grid>
        </Grid>
      </SoftBox>
      <Dialog open={Boolean(kpiModal)} onClose={() => setKpiModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <SoftBox display="flex" alignItems="center" gap={1}>
            {kpiModal && <Icon sx={{ color:`${KPI_DETAILS[kpiModal]?.iconColor}.main` }}>{KPI_DETAILS[kpiModal]?.icon}</Icon>}
            <SoftTypography variant="h6" fontWeight="medium" component="span">{KPI_DETAILS[kpiModal]?.title}</SoftTypography>
          </SoftBox>
        </DialogTitle>
        <DialogContent>{kpiModal && KPI_DETAILS[kpiModal]?.content()}</DialogContent>
        <DialogActions><SoftButton variant="text" color="secondary" onClick={() => setKpiModal(null)}>Close</SoftButton></DialogActions>
      </Dialog>

      <Footer company={{ href:"#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default FinanceDashboard;
